import { Suspense, useContext, useEffect, useRef, useMemo, useState } from "react";
import { useAgentTools } from "@/hooks/useAgentTools";
import { useAgentHandoff } from "@/hooks/useAgentHandoff";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { RESTORED_HOW_IT_WORKS_ALIAS } from "@/lib/restored-information-alias.mjs";
import { verifiedFaqAliasPath } from "@/lib/verified-lp-alias.mjs";
import { getCanonicalPublicOrigin } from "@/lib/canonical-origin";
import { isDriveshareLegacySeoEnabled } from "../../../lib/copy/src/legacy-seo-switch.mjs";
import { resolveReleaseRouteRedirect } from "../../../lib/copy/src/release-route-redirect-map.mjs";
import { releaseRouteDisposition } from "../../../lib/copy/src/release-route-dispositions.mjs";
import { Switch, Route, Router as WouterRouter, useLocation, useSearch, Redirect } from "wouter";
import { LocaleProvider, useLocale, isLocaleNeutralPath } from "@/i18n/LocaleContext";
import { isHireMakeSlug, getHireLanding, WEDDING_CUSTOM_SLUGS } from "@/content/geo/hireLandings";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient as sharedQueryClient } from "@/lib/queryClient";
import type { QueryClient } from "@tanstack/react-query";
import { SsrRequestContext, endSsrBootstrap, useSsrBootstrap, isSsrNotFound } from "@/lib/ssr-bootstrap";
import { HomeRoute } from "@/lib/home-route";
import {
  PublicCarRoute as CarDetailPage,
  PublicCarIdentityRoute as CarSlugRedirect,
  PublicSearchRoute as SearchPage,
  PublicCityGuideRoute as CityGuidePage,
  PublicHireLandingRoute as HireLandingPage,
  PublicModelGuideRoute as ModelGuidePage,
  PublicUseCaseGuideRoute as UseCaseGuidePage,
  PublicComparisonGuideRoute as ComparisonGuidePage,
  PublicModelsIndexRoute as ModelsIndexPage,
  PublicCitiesIndexRoute as CitiesIndexPage,
  PublicUseCasesIndexRoute as UseCasesIndexPage,
  PublicComparisonsIndexRoute as ComparisonsIndexPage,
  PublicLegacyCarRoute as LegacyCarPage,
  PublicCollectionRoute as LegacyCollectionPage,
  eagerLandingPage,
} from "@/lib/public-page-routes";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@workspace/ui/components/ui/tooltip";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { GuestAccountProfile, GuestBookingBoundary, GuestBookingClaimCoordinator, GuestEmailConfirmationNotice, GuestProfileCompletionBoundary } from "@/components/checkout/GuestBookingAccount";
import { AuthModalProvider } from "@/components/auth/AuthModalContext";
import { StepUpAuthDialog } from "@/components/auth/StepUpAuthDialog";
import { ImpersonationProvider } from "@/components/auth/ImpersonationProvider";
import { AppModeProvider, useAppMode, isGuestRoute, isHostRoute } from "@/contexts/AppModeContext";
import { FeatureFlagProvider, useFeatureFlag, useFeatureFlagLoading } from "@/hooks/useFeatureFlags";
import { CurrencyProvider } from "@/i18n/CurrencyContext";
import { PreferenceHydrator } from "@/i18n/PreferenceHydrator";
import { BrandRegistryBootstrap } from "@/lib/brand-registry-bootstrap";
import { RegionSuggestionBanner } from "@/components/layout/RegionSuggestionBanner";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { CheckoutEntry } from "@/components/checkout/CheckoutEntry";
import { RequireHost } from "@/components/auth/RequireHost";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DashboardErrorBoundary } from "@/components/dashboard/DashboardErrorBoundary";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BottomTabBar } from "@/components/layout/BottomTabBar";
import { DsIconSprite } from "@/components/ui/ds-icons";
import { PageLoader } from "@/components/ui/spinner";
import { OwnerDashboardProvider } from "@/hooks/useOwnerDashboard";
import { useReferralCapture } from "@/hooks/useReferralCapture";
import { useUser } from "@/hooks/useUser";
import { getMaintenanceAccess } from "@/lib/maintenance-api";
import { lazyRetry } from "@/lib/lazyRetry";
import { prefersCompiledPublicTemplate, readPublicTemplateDecision } from "@/lib/public-authored-source";
import { cmsTemplateOwnsPath } from "../../../lib/copy/src/public-cms-publication.mjs";
import { HELP_ARTICLE_ROUTE, matchPublicKnowledgePath } from "../../../lib/copy/src/help-article-path.mjs";
import { PublicKnowledgeRoute } from "@/lib/public-knowledge-routes";
import { prefersStaticPublicTemplate, PublicStaticRoute, staticPublicPath } from "@/lib/public-static-source";
import { page as trackPage } from "@/lib/analytics";
import { RecoveryModeRedirector } from "@/components/auth/RecoveryModeRedirector";
import { ConsentBanner } from "@/components/consent/ConsentBanner";
import { setLauncherVisibility, hideIntercom } from "@/lib/intercom";
import { ProdReadonlyBanner } from "@/components/ProdReadonlyBanner";
import { RENTER_PROMISE_FLAG, type FeatureFlagKey } from "@workspace/feature-flags-shared";
import "@/pages/JourneyPresentation.css";
import "@/components/layout/PageCanvas.css";
import { PlatformProvider, BrandCutoverSync, usePlatform } from "@/components/platform/PlatformProvider";
import { getPlatformConfig } from "@/lib/platform/config";
import { MARKETS_BY_CODE } from "@/i18n/markets";
import { isKnownGlobalLandingRestorationPath } from "@/lib/global-landing-restoration-paths";
import { resolveFaqAlias, faqAliasNonMainHostTarget } from "@/lib/faq-alias";
import { usePublicCanonicalRoute } from "@/lib/public-canonical-route";
import { apexEditorialRedirect } from "../../../lib/copy/src/apex-editorial-route.mjs";

const HomePage = HomeRoute;
const ConciergeConversationPage = lazyRetry(() => import("@/pages/ConciergeConversationPage"));
const ConciergePage = lazyRetry(() => import("@/pages/ConciergePage"));
import { NotFoundRoute as NotFound } from "@/lib/public-page-routes";
const MaintenancePage = lazyRetry(() => import("@/pages/MaintenancePage"));


const HowItWorksPage = lazyRetry(() => import("@/pages/HowItWorksPage"));
const PromisePage = lazyRetry(() => import("@/pages/PromisePage"));
const InsurancePage = lazyRetry(() => import("@/pages/InsurancePage"));
const ListYourCarPage = lazyRetry(() => import("@/pages/ListYourCarPage"));
const AuthRedirect = lazyRetry(() => import("@/pages/AuthRedirect"));
const ForgotPasswordPage = lazyRetry(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyRetry(() => import("@/pages/ResetPasswordPage"));
const AuthCallbackPage = lazyRetry(() => import("@/components/auth/AuthCallbackPage"));
const SignInConfirmPage = lazyRetry(() => import("@/pages/SignInConfirmPage"));
const DashboardPage = lazyRetry(() => import("@/pages/DashboardPage"));
const MyListingsPage = lazyRetry(() => import("@/pages/MyListingsPage"));
const ListingWizardPage = lazyRetry(() => import("@/pages/ListingWizardPage"));
const ListingEditPage = lazyRetry(() => import("@/pages/ListingEditPage"));
const CheckoutPage = lazyRetry(() => import("@/pages/CheckoutPage"));
function FreshCheckoutRoute() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  return <CheckoutEntry><CheckoutPage key={`${params.get("car")}:${params.get("h") || "initial"}`} /></CheckoutEntry>;
}
const CheckoutResumePage = lazyRetry(() => import("@/pages/CheckoutResumePage"));
const RetryPaymentPage = lazyRetry(() => import("@/pages/RetryPaymentPage"));
const ClaimInvitePage = lazyRetry(() => import("@/pages/ClaimInvitePage"));
const DriverInvitationPage = lazyRetry(() => import("@/pages/DriverInvitationPage"));
const WinBackPage = lazyRetry(() => import("@/pages/WinBackPage"));
const ProbeRespondPage = lazyRetry(() => import("@/pages/ProbeRespondPage"));
const ClaimPayPage = lazyRetry(() => import("@/pages/ClaimPayPage"));
// Task #20021 — app-less handover magic-link page (token-authed, dark-gated
// server-side; links only exist under the DSv2 cutover gate).
const HandoverWebPage = lazyRetry(() => import("@/pages/HandoverWebPage"));
// Audit 2026-08-21 (P0-1) — browser return page for the hosted Didit flow
// (DIDIT_RETURN_URL points here once the console webhook URL is confirmed).
const VerifyReturnPage = lazyRetry(() => import("@/pages/VerifyReturnPage"));
const BookingConfirmationPage = lazyRetry(() => import("@/pages/BookingConfirmationPageV2"));
const BookingVerificationPage = lazyRetry(() => import("@/pages/BookingVerificationPage"));
const VerificationPage = lazyRetry(() => import("@/pages/VerificationPage"));
const TripsPage = lazyRetry(() => import("@/pages/TripsPage"));
const TripDetailPage = lazyRetry(() => import("@/pages/TripDetailPage"));
const OwnerBookingDetailPage = lazyRetry(() => import("@/pages/OwnerBookingDetailPage"));
const OwnerBookingsPage = lazyRetry(() => import("@/pages/OwnerBookingsPage"));
const EarningsPage = lazyRetry(() => import("@/pages/EarningsPage"));
const AccountPayoutsPage = lazyRetry(() => import("@/pages/AccountPayoutsPage"));
// Task #2833 (Flow Review 05, Finding C) — public landing page for the
// payout-email confirmation link emailed to the override address.
const AccountPayoutsVerifyEmailPage = lazyRetry(() => import("@/pages/AccountPayoutsVerifyEmailPage"));
const PayoutLinkLandingPage = lazyRetry(() => import("@/pages/PayoutLinkLandingPage"));
const ConversationPage = lazyRetry(() => import("@/pages/ConversationPage"));

const InboxPage = lazyRetry(() => import("@/pages/InboxPage"));
const FavouritesPage = lazyRetry(() => import("@/pages/FavouritesPage"));
const ReviewsPage = lazyRetry(() => import("@/pages/ReviewsPage"));
const NotificationsPage = lazyRetry(() => import("@/pages/NotificationsPage"));
const SettingsPage = lazyRetry(() => import("@/pages/SettingsPage"));
const HostProfilePage = lazyRetry(() => import("@/pages/HostProfilePage"));
const DashboardCalendarPage = lazyRetry(() => import("@/pages/DashboardCalendarPage"));
const ProfilePage = lazyRetry(() => import("@/pages/ProfilePage"));
const UserProfilePage = lazyRetry(() => import("@/pages/UserProfilePage"));
// Public SSR never serves authenticated admin routes. Exclude their editor
// dependency graph (including jsdom) from the public server compilation;
// the browser still loads the exact same AdminPage.
const AdminPage = lazyRetry(() => import.meta.env.SSR
  ? Promise.reject(new Error("Admin routes require client rendering"))
  : import("@/pages/AdminPage"));
const SpotlightDetailPage = lazyRetry(() => import("@/pages/admin/SpotlightDetailPage"));
const ClaimDetailPage = lazyRetry(() => import("@/pages/admin/ClaimDetailPage"));
const AboutPage = lazyRetry(() => import("@/pages/AboutPage"));

const AccountSignUpPage = lazyRetry(() => import("@/pages/AccountSignUpPage"));
const CompanyPage = lazyRetry(() => import("@/pages/CompanyPage"));
const ListACarPage = lazyRetry(() => import("@/pages/ListACarPage"));
const WhyListPage = lazyRetry(() => import("@/pages/WhyListPage"));
const FaqRenterPage = lazyRetry(() => import("@/pages/FaqRenterPage"));
const FaqProceduresPage = lazyRetry(() => import("@/pages/FaqProceduresPage"));

const FaqAliasPage = lazyRetry(() => import("@/pages/FaqAliasPage"));
const HelpPage = () => <PublicKnowledgeRoute type="help-index" />;
const HelpTopicPage = () => <PublicKnowledgeRoute type="help-topic" />;
const HelpArticlePage = () => <PublicKnowledgeRoute type="help-article" />;
const HelpSearchPage = () => <PublicKnowledgeRoute type="help-search" />;
const HelpContactPage = () => <PublicKnowledgeRoute type="help-contact" />;
const TermsPage = lazyRetry(() => import("@/pages/TermsPage"));
const PrivacyPage = lazyRetry(() => import("@/pages/PrivacyPage"));
const UnsubscribePage = lazyRetry(() => import("@/pages/UnsubscribePage"));
const PoliciesPage = lazyRetry(() => import("@/pages/PoliciesPage"));
const OnboardingPage = lazyRetry(() => import("@/pages/OnboardingPage"));
const ProOwnerPage = lazyRetry(() => import("@/pages/ProOwnerPage"));
const ProfessionalPage = lazyRetry(() => import("@/pages/ProfessionalPage"));
const ReferralPage = lazyRetry(() => import("@/pages/ReferralPage"));
const ReferAHostPage = lazyRetry(() => import("@/pages/ReferAHostPage"));
const ReferralExpiredPage = lazyRetry(() => import("@/pages/ReferralExpiredPage"));
const DashboardSubscriptionPage = lazyRetry(() => import("@/pages/DashboardSubscriptionPage"));
const DashboardAnalyticsPage = lazyRetry(() => import("@/pages/DashboardAnalyticsPage"));
const DashboardDriversPage = lazyRetry(() => import("@/pages/DashboardDriversPage"));
// Tasks #20024/#20025 — DSv2 host claims tracker (dark until cutover: the
// routes are double flag-gated AND every claims endpoint 404s while off).
const DashboardClaimsPage = lazyRetry(() => import("@/pages/DashboardClaimsPage"));
const DashboardClaimDetailPage = lazyRetry(() => import("@/pages/DashboardClaimDetailPage"));
const LazyLandingPageCatchAll = lazyRetry(() => import("@/pages/LandingPageCatchAll"));

// Task #2431 — DEV-only preview route for the post-submit hub. Bundled
// via `lazyRetry` so it doesn't sit in the main chunk, and gated by
// `import.meta.env.DEV` at the route level below so production
// bundles never reach it.
const PostSubmitHubDevPreview = lazyRetry(() => import("@/pages/dev/PostSubmitHubDevPreview"));
// Task #4507 — DEV-only harness that renders four mobile-web surfaces at a
// phone viewport so a Playwright visual check can assert no element overlap /
// clipping (jsdom has no layout engine). Gated by `import.meta.env.DEV` at the
// route below so it never ships in a production bundle.
const LayoutHarnessPage = lazyRetry(() => import("@/pages/dev/LayoutHarnessPage"));

// Task #2383 — GEO editorial pages (model / city / use-case / comparison guides).

/**
 * Task #6522 — /hire/:slug serves two families: country-scoped hire-landing
 * make hubs (e.g. /uk/hire/mercedes-benz) and the worldwide use-case guides
 * (e.g. /hire/wedding-car-hire). Registry lookup for the current market
 * decides which page renders.
 */
function HireSlugSwitch({ params }: { params: { slug: string } }) {
  const { market } = useLocale();
  if (market && isHireMakeSlug(market, params.slug)) {
    return <HireLandingPage />;
  }
  return <UseCaseGuidePage />;
}

/**
 * Task #7156 — native-language wedding slugs (e.g. /dk/bryllupsbil,
 * /de/hochzeitsauto-mieten). Only markets with a registry entry render the
 * curated page; every other market falls through to the DB-backed landing
 * page catch-all so legacy `landing_pages` rows at the same slug keep
 * resolving (e.g. an old /fr row sharing a slug).
 */
function WeddingSlugSwitch({ slug }: { slug: string }) {
  const LandingPageCatchAll = eagerLandingPage(LazyLandingPageCatchAll);
  const { market } = useLocale();
  if (market === "dk" && slug === "bryllupsbil") {
    return <LandingPageCatchAll fallbackOnMiss={<HireLandingPage />} />;
  }
  if (market === "de" && slug === "hochzeitsauto-mieten") {
    return <LandingPageCatchAll />;
  }
  if (market && getHireLanding(market, `/${slug}`)) {
    return <HireLandingPage />;
  }
  return <LandingPageCatchAll />;
}

// Task #4409 — editorial Guide Hub (index / category / article).
const GuidesIndexPage = () => <PublicKnowledgeRoute type="guide-index" />;
const GuideCategoryPage = () => <PublicKnowledgeRoute type="guide-category" />;
const GuideArticlePage = () => <PublicKnowledgeRoute type="guide-article" />;


// Task #3560/#3561 — paint the top safe-area inset brand-green on phones so
// EVERY mobile screen shows a green status-bar strip, even the slim
// light-header browse/search screens. In a standalone (added-to-home-screen)
// PWA the web view draws under the notch; this opaque strip guarantees the
// notch area is brand-green rather than white. `env(safe-area-inset-top)` is
// 0 on desktop + non-notch devices and the element is `md:hidden`, so desktop
// chrome stays byte-for-byte unchanged.
function SafeAreaTopFill() {
  return (
    <div
      aria-hidden="true"
      className="fixed inset-x-0 top-0 z-[60] bg-brand md:hidden"
      style={{ height: "env(safe-area-inset-top)" }}
    />
  );
}

function FlagGate({ flag, children }: { flag: FeatureFlagKey; children: React.ReactNode }) {
  const enabled = useFeatureFlag(flag);
  const loading = useFeatureFlagLoading();
  if (loading) return <PageLoader />;
  if (!enabled) return <NotFound />;
  return <>{children}</>;
}

function Router() {
  const LandingPageCatchAll = eagerLandingPage(LazyLandingPageCatchAll);
  const { market, innerPath } = useLocale();
  const { platform } = usePlatform();
  const basePath = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
  const ssrRequest = useContext(SsrRequestContext);
  const pathname = ssrRequest ? ssrRequest.pathname : typeof window === "undefined" ? "" : window.location.pathname;
  const routePath = basePath && (pathname === basePath || pathname.startsWith(`${basePath}/`))
    ? pathname.slice(basePath.length) || "/"
    : pathname;
  const isKnownRestorationPath = isKnownGlobalLandingRestorationPath(routePath);
  const isMarketRoot = !!market && innerPath === "/";
  const staticPath = staticPublicPath(routePath);
  const templateInputs = { seed: ssrRequest?.seed, decision: readPublicTemplateDecision() };
  const compiledTemplate = !!matchPublicKnowledgePath(routePath) ||
    prefersStaticPublicTemplate(routePath, templateInputs) || prefersCompiledPublicTemplate(routePath, {
    seed: ssrRequest?.seed, decision: readPublicTemplateDecision(),
  });
  const canonicalRoute = usePublicCanonicalRoute(routePath);
  const apexRedirect = apexEditorialRedirect(routePath);
  if (apexRedirect) return <Redirect to={`~${basePath}${apexRedirect}`} replace />;
  // Keep every hook unconditional across the transition away from a 404.
  if (isSsrNotFound(ssrRequest, pathname)) return <Suspense fallback={<PageLoader />}><NotFound /></Suspense>;
  // A current published resolver-owned CMS source outranks historical client
  // redirects as well as static templates. Redirect can render null on SSR.
  if (cmsTemplateOwnsPath(routePath, templateInputs)) {
    return <Suspense fallback={<PageLoader />}><LandingPageCatchAll /></Suspense>;
  }
  if (!compiledTemplate && canonicalRoute.error) throw canonicalRoute.error;
  if (!compiledTemplate && canonicalRoute.pending) return <PageLoader />;
  const retainedFaqPath = verifiedFaqAliasPath(routePath,
    typeof window === "undefined" ? "" : window.location.hostname,
    isDriveshareLegacySeoEnabled(), getCanonicalPublicOrigin());
  if (retainedFaqPath) return <FaqAliasPage
    alias={retainedFaqPath === "/faq-renter/" ? "renter" : "procedures"}
    retainedCanonicalPath={retainedFaqPath} />;
  const releaseTarget = resolveReleaseRouteRedirect(routePath, { countryRoot: market || "" });
  if (releaseTarget) return <Redirect to={`~${basePath}${releaseTarget}`} />;
  if (releaseRouteDisposition(routePath) === "legacy-404") return <NotFound />;
  // These are real public template routes, not missing CMS landings. They
  // must survive hydration without a by-path miss or an address-bar rewrite.
  if (innerPath.replace(/\/+$/, "") === "/why-list") {
    return <Suspense fallback={<PageLoader />}><WhyListPage /></Suspense>;
  }
  if (market && innerPath.replace(/\/+$/, "") === "/cars") {
    return <Suspense fallback={<PageLoader />}><SearchPage /></Suspense>;
  }
  if (!compiledTemplate && canonicalRoute.page?.url &&
      (canonicalRoute.page.kind === "landing" || canonicalRoute.page.kind === "info")) {
    return <Suspense fallback={<PageLoader />}>
      {routePath.replace(/\/+$/, "") === "/privacy-notice" ? <PrivacyPage /> :
        routePath.replace(/\/+$/, "") === "/terms-of-service" ? <TermsPage /> :
          <LandingPageCatchAll />}
    </Suspense>;
  }
  // Do not turn an absent qualified publication into the global English
  // template during client navigation after the same-country CMS lookup.
  if (market && routePath !== innerPath && matchPublicKnowledgePath(innerPath) &&
      !canonicalRoute.page) return <NotFound />;
  if (staticPath) return <PublicStaticRoute pathname={staticPath} />;

  // Restore exact source paths before legacy/product/blog route fallbacks.
  // Market roots delegate through HomePage below so its existing homepage
  // remains the fallback; the US "/" root retains its stronger hero/SEO.
  if (!compiledTemplate && isKnownRestorationPath && routePath !== "/" && !isMarketRoot) {
    return (
      <Suspense fallback={<PageLoader />}>
        <LandingPageCatchAll />
      </Suspense>
    );
  }

  // The brand flag can resolve after the first render. Use the stable host
  // for retained legacy paths so an initial "bac" render cannot redirect
  // them before the flag switches.
  const dsDomain = getPlatformConfig("driveshare").domain;
  const onLegacyMainHost = typeof window !== "undefined"
    && (window.location.hostname === dsDomain || window.location.hostname === `www.${dsDomain}`);
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/">
          {() => <HomePage />}
        </Route>
        <Route path="/search" component={SearchPage} />
        {/* Stable, public-safe reply destination for concierge notifications
            and Intercom email CTAs. The page opens an identified Messenger
            inbox when available and always exposes an email fallback. */}
        <Route path="/concierge" component={ConciergePage} />
        <Route path="/concierge/conversations/:conversationId" component={ConciergeConversationPage} />
        {/* Bare `/cars` has no index page — fold it into the canonical
         * marketplace at `/search`. (Prod also 301s this at the edge in
         * serve-static.mjs; this covers dev + in-app client navigation.) */}
        <Route path="/cars">{() => <Redirect to="/search" replace />}</Route>
        {/* Task #6161 — legacy host-registration URL. The edge 301 in
         * serve-static.mjs handles crawlers/direct hits; this covers dev
         * preview + in-app client navigation. */}
        <Route path="/register">{() => <Redirect to="/list-your-car" replace />}</Route>
        <Route path="/cars/:id/:slug" component={CarDetailPage} />
        {/* The stable public US identity deliberately renders
            in place instead of redirecting to a mutable /cars/:id/:slug URL. */}
        <Route path="/car/:legacySlug/home" component={LegacyCarPage} />
        <Route path="/car/:legacySlug/search" component={LegacyCarPage} />
        <Route path="/car/:legacySlug" component={LegacyCarPage} />
        <Route path="/car/:legacySlug/home" component={LegacyCarPage} />
        {/*
         * Slug-less car URL fallback. Hosts and visitors sometimes type
         * just the id (e.g. `/uk/cars/1046495` from a Slack message or
         * the URL bar) — without this route the SPA falls through to the
         * global 404 even though we have the data to resolve it. The
         * component fetches the canonical slug + locale prefix and
         * `replace`-navigates to `/cars/:id/:slug`.
         */}
        <Route path="/cars/:id" component={CarSlugRedirect} />
        <Route path="/how-it-works" component={HowItWorksPage} />
        <Route path={RESTORED_HOW_IT_WORKS_ALIAS}>
          {() => <Redirect to={`/how-it-works${window.location.search}`} replace />}
        </Route>
        <Route path="/promise">
          {() => (
            <FlagGate flag={RENTER_PROMISE_FLAG}>
              <PromisePage />
            </FlagGate>
          )}
        </Route>
        <Route path="/insurance" component={InsurancePage} />
        <Route path="/list-your-car" component={ListYourCarPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/company" component={CompanyPage} />
        <Route path="/refer-a-host/expired" component={ReferralExpiredPage} />
        <Route path="/refer-a-host">
          {() => (
            <FlagGate flag="feature_referrals">
              <ReferAHostPage />
            </FlagGate>
          )}
        </Route>
        <Route path="/why-list" component={WhyListPage} />
        <Route path="/list-a-car" component={ListACarPage} />
        {/* Task #2383 — GEO editorial pages: model, city, use-case, and
            comparison guides. Indexes plus per-slug detail pages. */}
        <Route path="/models" component={ModelsIndexPage} />
        <Route path="/models/:slug" component={ModelGuidePage} />
        <Route path="/cities" component={CitiesIndexPage} />
        <Route path="/cities/:slug" component={CityGuidePage} />
        <Route path="/hire" component={UseCasesIndexPage} />
        {/* Task #6522 — country-scoped hire landing pages. The two-segment
            make/model route must be registered BEFORE the single-segment
            use-case guide matcher. Single-segment /hire/x is disambiguated
            inside HireSlugSwitch: hire-landing make pages (registry-backed
            for the current market) win over worldwide use-case guides. */}
        <Route path="/hire/:make/:model" component={HireLandingPage} />
        <Route path="/hire/:slug" component={HireSlugSwitch} />
        <Route path="/wedding-car-hire">
          <WeddingSlugSwitch slug="wedding-car-hire" />
        </Route>
        {/* Task #7156 — native-language wedding landing slugs. */}
        {WEDDING_CUSTOM_SLUGS.map((s) => (
          <Route key={s} path={`/${s}`}>
            {() => <WeddingSlugSwitch slug={s} />}
          </Route>
        ))}
        <Route path="/compare" component={ComparisonsIndexPage} />
        <Route path="/compare/:slug" component={ComparisonGuidePage} />
        {/* Task #4409 — editorial Guide Hub. Register the category route
            BEFORE the article route so /guides/category/:slug is not
            captured by the single-segment /guides/:slug article matcher. */}
        <Route path="/guides" component={GuidesIndexPage} />
        <Route path="/guides/category/:slug" component={GuideCategoryPage} />
        <Route path="/guides/:slug" component={GuideArticlePage} />
        {/*
         * Task #1236 — the new Airbnb-style /help Help Centre supersedes
         * the legacy renter/procedures FAQ pages and the long-form
         * /policies dump. Old URLs redirect to the closest topic so any
         * external links, sitemaps and Intercom-Fin training data keep
         * resolving (301-equivalent client redirect — wouter's <Redirect>
         * uses replaceState so search engines treat it as canonical-rewrite).
         */}
        <Route path="/help" component={HelpPage} />
        <Route path="/verify/return" component={VerifyReturnPage} />
        <Route path="/help/search" component={HelpSearchPage} />
        <Route path="/help/contact" component={HelpContactPage} />
        <Route path="/help/topic/:topic" component={HelpTopicPage} />
        <Route path={HELP_ARTICLE_ROUTE} component={HelpArticlePage} />
        <Route path={/^\/faq\/(?:renter|procedures)\/?$/}>
          {() => {
            const alias = typeof window === "undefined"
              ? null
              : resolveFaqAlias(window.location.pathname);
            if (!alias) return <NotFound />;
            return onLegacyMainHost
              ? <FaqAliasPage alias={alias} />
              : <Redirect to={faqAliasNonMainHostTarget(alias, window.location.search)} replace />;
          }}
        </Route>
        <Route path={/^\/faq-renter\/?$/}>
          {() => onLegacyMainHost ? <LandingPageCatchAll /> : <Redirect to="/help" replace />}
        </Route>
        <Route path={/^\/faq-procedures\/?$/}>
          {() => onLegacyMainHost ? <LandingPageCatchAll /> : <Redirect to="/help/topic/booking-a-car" replace />}
        </Route>
        <Route path="/policies">
          {() => onLegacyMainHost ? <LandingPageCatchAll /> : <Redirect to="/help/topic/trust-and-safety" replace />}
        </Route>
        {/* Legacy components kept for the admin live-preview tool only. */}
        <Route path="/legacy/faq-renter" component={FaqRenterPage} />
        <Route path="/legacy/faq-procedures" component={FaqProceduresPage} />
        <Route path="/legacy/policies" component={PoliciesPage} />
        <Route path="/terms">
          {() => <PublicStaticRoute pathname="/terms" />}
        </Route>
        <Route path="/privacy">
          {() => <PublicStaticRoute pathname="/privacy" />}
        </Route>
        <Route path="/privacy-notice">
          {() => onLegacyMainHost ? <PrivacyPage /> : <NotFound />}
        </Route>
        <Route path="/faq-clubs">
          {() => <LandingPageCatchAll />}
        </Route>
        <Route path="/unsubscribe" component={UnsubscribePage} />
        <Route path="/auth">{() => <AuthRedirect intent="auto" />}</Route>
        <Route path="/login">{() => <AuthRedirect intent="signin" />}</Route>
        <Route path="/signup">{() => <AuthRedirect intent="signup" />}</Route>
        <Route path={/^\/sign-up\/?$/}>
          {() => onLegacyMainHost || platform === "driveshare" ? <AccountSignUpPage /> : <LandingPageCatchAll />}
        </Route>
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/reset-password" component={ResetPasswordPage} />
        <Route path="/auth/callback" component={AuthCallbackPage} />
        {/* Task #6311 — click-to-consume sign-in link interstitial (scanner-proof). */}
        <Route path="/auth/confirm-signin" component={SignInConfirmPage} />
        <Route path="/pro-owner" component={ProOwnerPage} />
        <Route path="/professional" component={ProfessionalPage} />
        <Route path="/collection/:sourceId" component={LegacyCollectionPage} />
        <Route path={/^\/public-profile\/28207\/$/}>
          {() => <LegacyCollectionPage sourceIdOverride="28207" />}
        </Route>
        <Route path="/profile/:userId" component={ProfilePage} />

        {/* User-facing routes (outside dashboard, with header+footer) */}
        <Route path="/trips">
          {() => (
            <RequireAuth allowCheckoutGuest>
              <GuestProfileCompletionBoundary>
                <div className="mx-auto max-w-6xl px-4 pt-6"><GuestEmailConfirmationNotice /></div>
                <TripsPage />
              </GuestProfileCompletionBoundary>
            </RequireAuth>
          )}
        </Route>
        {/* Compatibility for review CTAs sent before the canonical route used
            ?review=1. Keep old email/chat links working instead of rendering
            the generic 404 page. */}
        <Route path="/trips/:publicId/review">
          {(params: { publicId: string }) => (
            <Redirect to={`/trips/${params.publicId}?review=1`} replace />
          )}
        </Route>
        <Route path="/verification">
          {() => (
            <RequireAuth>
              <VerificationPage />
            </RequireAuth>
          )}
        </Route>
        {/* Stable first-party handoff used by email, QR and Trips. Keep the
            verification action separate from the booking confirmation page. */}
        <Route path="/trips/:publicId/verification">
          {(params: { publicId: string }) => (
            <RequireAuth allowCheckoutGuest>
              <GuestBookingBoundary publicId={params.publicId}><BookingVerificationPage /></GuestBookingBoundary>
            </RequireAuth>
          )}
        </Route>
        <Route path="/trips/:publicId">
          {(params: { publicId: string }) => (
            <RequireAuth allowCheckoutGuest>
              <GuestBookingBoundary publicId={params.publicId}><TripDetailPage /></GuestBookingBoundary>
            </RequireAuth>
          )}
        </Route>
        <Route path="/profile">
          {() => (
            <RequireAuth allowCheckoutGuest>
              <GuestAccountProfile><UserProfilePage /></GuestAccountProfile>
            </RequireAuth>
          )}
        </Route>
        <Route path="/favourites">
          {() => (
            <RequireAuth>
              <FavouritesPage />
            </RequireAuth>
          )}
        </Route>
        <Route path="/settings">
          {() => (
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          )}
        </Route>
        <Route path="/messages">
          {() => (
            <RequireAuth allowCheckoutGuest>
              <ConversationPage />
            </RequireAuth>
          )}
        </Route>
        <Route path="/messages/:publicId">
          {(params) => (
            <RequireAuth allowCheckoutGuest>
              <GuestBookingBoundary publicId={params.publicId}><ConversationPage /></GuestBookingBoundary>
            </RequireAuth>
          )}
        </Route>
        {/* The legacy inbox URL remains an in-place account gate.
            Signed-in visitors see the existing messages experience; logged-out
            visitors stay on this URL and can open the standard sign-in flow. */}
        <Route path="/inbox" component={InboxPage} />
        <Route path="/notifications">
          {() => (
            <RequireAuth>
              <NotificationsPage />
            </RequireAuth>
          )}
        </Route>

        {/* Redirects from old /dashboard/* paths to new user-facing routes */}
        {/* Note (#2382): we previously had explicit trailing-slash redirect
            routes here (`/dashboard/listings/`, `/dashboard/listings/new/`,
            `/dashboard/`). wouter v3 normalises trailing slashes when
            matching, so those patterns ALSO captured the bare no-slash
            variants and re-emitted a `<Redirect to="/dashboard">` — which
            then matched the same route again, producing a silent
            no-op redirect loop and a blank screen on /dashboard and the
            listings wizard. The canonical no-slash routes below handle
            trailing-slash email links directly via wouter's normaliser. */}
        <Route path="/dashboard/trips">{() => <Redirect to="/trips" />}</Route>
        <Route path="/dashboard/trips/:publicId">
          {(params: { publicId: string }) => <Redirect to={`/trips/${params.publicId}`} />}
        </Route>
        <Route path="/dashboard/favourites">{() => <Redirect to="/favourites" />}</Route>
        <Route path="/dashboard/settings">{() => <Redirect to="/settings" />}</Route>
        <Route path="/dashboard/messages">{() => <Redirect to="/messages" />}</Route>
        <Route path="/dashboard/messages/:publicId">
          {(params: { publicId: string }) => <Redirect to={`/messages/${params.publicId}`} />}
        </Route>
        {/* Task #6224 — legacy password-reset URLs. Old BookAClassic links,
            bookmarks, and Google results point at `/dashboard/password/reset`
            (and market-prefixed / trailing-slash variants such as
            `/uk/dashboard/password/reset/`). The new auth flow only exposes
            `/forgot-password` and `/reset-password`, so these paths otherwise
            fell through to LandingPageCatchAll and died on a dead 404 with no
            working reset form. The `:rest*` wildcard also captures any sibling
            `/dashboard/password/*` path. Market prefixes are stripped by the
            LocaleAwareRouterBase before matching, so no prefixed variants are
            needed here. */}
        <Route path="/dashboard/password/:rest*">{() => <Redirect to="/forgot-password" />}</Route>

        {/* Host dashboard routes (unchanged) */}
        {/* Onboarding is a transition route — non-hosts must be able to
            reach it to *become* hosts. We still route through RequireHost
            with allowListingWizard=true so every /dashboard/* path uses a
            single canonical wrapper; that closes the future-drift loophole
            (forgetting RequireHost on a new host page) flagged in #1752
            review. The exception prop makes the host bypass explicit. */}
        <Route path="/dashboard/onboarding">
          {() => (
            <RequireHost allowListingWizard>
              <DashboardErrorBoundary route="/dashboard/onboarding">
                <OnboardingPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard">
                <DashboardPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/account/payouts">
          {() => (
            <RequireAuth>
              <AccountPayoutsPage />
            </RequireAuth>
          )}
        </Route>
        {/* Task #2833 — public confirmation-link landing. Intentionally
            NOT wrapped in RequireAuth: the link may be opened by a
            finance/accountant inbox that doesn't have a BookAclassic
            login. Security rests on the unguessable token. */}
        <Route path="/account/payouts/verify-email">
          {() => <AccountPayoutsVerifyEmailPage />}
        </Route>
        <Route path="/payout-link/:token">
          {() => <PayoutLinkLandingPage />}
        </Route>
        <Route path="/dashboard/calendar">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/calendar">
                <DashboardCalendarPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/referrals">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/referrals">
                <FlagGate flag="feature_referrals">
                  <ReferralPage />
                </FlagGate>
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/listings">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/listings">
                <MyListingsPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/listings/new">
          {() => (
            <RequireHost allowListingWizard>
              <DashboardErrorBoundary route="/dashboard/listings/new">
                <ListingWizardPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/listings/:id/resume">
          {() => (
            <RequireHost allowListingWizard>
              <DashboardErrorBoundary route="/dashboard/listings/:id/resume">
                <ListingWizardPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/listings/:id/edit">
          {() => (
            <RequireHost allowListingWizard>
              <DashboardErrorBoundary route="/dashboard/listings/:id/edit">
                <ListingEditPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/checkout">
          {() => <FreshCheckoutRoute />}
        </Route>
        <Route path="/checkout/retry/:publicId">
          {() => (
            <RequireAuth>
              <RetryPaymentPage />
            </RequireAuth>
          )}
        </Route>
        <Route path="/checkout/resume/:publicId">
          {(params) => <RequireAuth allowCheckoutGuest>
            <GuestBookingBoundary publicId={params.publicId}><CheckoutResumePage /></GuestBookingBoundary>
          </RequireAuth>}
        </Route>
        <Route path="/win-back/preview" component={WinBackPage} />
        <Route path="/win-back/:token" component={WinBackPage} />
        <Route path="/handover/:token" component={HandoverWebPage} />
        <Route path="/probe-respond/preview" component={ProbeRespondPage} />
        <Route path="/probe-respond/:token" component={ProbeRespondPage} />
        <Route path="/bookings/claim/:token">
          {() => (
            <RequireAuth>
              <ClaimInvitePage />
            </RequireAuth>
          )}
        </Route>
        <Route path="/driver-invitation" component={DriverInvitationPage} />
        <Route path="/bookings/:publicId/pay-claim">
          {() => (
            <RequireAuth>
              <ClaimPayPage />
            </RequireAuth>
          )}
        </Route>
        <Route path="/bookings/preview" component={BookingConfirmationPage} />
        {/* Legacy /bookings/v2/:publicId — kept as redirect so old links/notifications keep working. */}
        <Route path="/bookings/v2/:publicId">
          {(params: { publicId: string }) => <Redirect to={`/bookings/${params.publicId}`} replace />}
        </Route>
        <Route path="/bookings/:publicId">
          {(params: { publicId: string }) => (
            <RequireAuth allowCheckoutGuest>
              <GuestBookingBoundary publicId={params.publicId}>
                <BookingConfirmationPage />
              </GuestBookingBoundary>
            </RequireAuth>
          )}
        </Route>
        <Route path="/dashboard/bookings">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/bookings">
                <OwnerBookingsPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        {/* Host-side single booking detail. Uses a dedicated host-only
            card-stack page so host workflows (counter-proposals, inspections,
            host cancel/decline) get a first-class layout while the renter
            page keeps its calm map-anchored design unchanged. */}
        <Route path="/dashboard/bookings/:publicId">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/bookings/:publicId">
                <OwnerBookingDetailPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/earnings">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/earnings">
                <EarningsPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/reviews">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/reviews">
                <ReviewsPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/notifications">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/notifications">
                <NotificationsPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/drivers">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/drivers">
                <FlagGate flag="feature_pro_owner">
                  <DashboardDriversPage />
                </FlagGate>
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        {/* Tasks #20024/#20025 — claims tracker, gated on BOTH cutover flags
            (matches the server's claimsFeatureEnabled: brand AND kill-switch).
            Fixes the historically dangling /dashboard/claims reminder links. */}
        <Route path="/dashboard/claims">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/claims">
                <FlagGate flag="brand_cutover_driveshare">
                  <FlagGate flag="feature_insurance_claims_v2">
                    <DashboardClaimsPage />
                  </FlagGate>
                </FlagGate>
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/claims/:id">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/claims/:id">
                <FlagGate flag="brand_cutover_driveshare">
                  <FlagGate flag="feature_insurance_claims_v2">
                    <DashboardClaimDetailPage />
                  </FlagGate>
                </FlagGate>
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/subscription">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/subscription">
                <DashboardSubscriptionPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/analytics">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/analytics">
                <DashboardAnalyticsPage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/dashboard/profile">
          {() => (
            <RequireHost>
              <DashboardErrorBoundary route="/dashboard/profile">
                <HostProfilePage />
              </DashboardErrorBoundary>
            </RequireHost>
          )}
        </Route>
        <Route path="/admin">
          {() => (
            <RequireAuth requiredRole={1}>
              <AdminPage />
            </RequireAuth>
          )}
        </Route>
        {/* Task #1494 — Pro Owner Marketing Spotlight detail page must be
            registered BEFORE /admin/:section so wouter matches the
            two-segment path first. */}
        <Route path="/admin/spotlight/:userId">
          {() => (
            <RequireAuth requiredRole={1}>
              <SpotlightDetailPage />
            </RequireAuth>
          )}
        </Route>
        {/* Task #20046 — full-screen Claims Agent Desk. Registered BEFORE
            /admin/:section so wouter matches the two-segment path first. */}
        <Route path="/admin/claims/:claimId">
          {() => (
            <RequireAuth requiredRole={1}>
              <ClaimDetailPage />
            </RequireAuth>
          )}
        </Route>
        <Route path="/admin/:section">
          {() => (
            <RequireAuth requiredRole={1}>
              <AdminPage />
            </RequireAuth>
          )}
        </Route>

        {/*
         * /us/* soft-handoff routes (task #677, follow-up to flag consolidation).
         *
         * `parseLocaleFromPath` rejects "us" as a market — historically the
         * server middleware + LocaleProvider hard-301'd `/us/*` to driveshare.com
         * via the cross-brand redirect. That redirect is now gated on
         * `feature_us_hide_from_search` (default OFF).
         *
         * When the flag is OFF, `/us/*` falls through to the SPA. Without these
         * routes, every US car link generated by `buildCarPath` (search results,
         * trip details, conversations, sitemap, JSON-LD) would 404. Mounting
         * the same components under `/us/*` keeps the page in BAC chrome and
         * lets the existing `feature_us_redirect_car_detail` flag swap the
         * booking widget for `DriveShareHandoffCard` when the staged rollout
         * reaches stage 2.
         */}
        <Route path="/us" component={HomePage} />
        <Route path="/us/search" component={SearchPage} />
        <Route path="/us/cars" component={SearchPage} />
        <Route path="/us/why-list" component={WhyListPage} />
        <Route path="/us/cars/:id/:slug" component={CarDetailPage} />
        {/* Slug-less /us/cars/<id> mirror of the worldwide redirect (see
         * /cars/:id route above). `parseLocaleFromPath` does not strip
         * `/us`, so the bare `/cars/:id` route never matches under it. */}
        <Route path="/us/cars/:id" component={CarSlugRedirect} />

        {/* Task #4299 — the maintenance holding page is always previewable at
            /maintenance regardless of the `feature_maintenance_mode` flag. When
            the flag is ON, AppShell renders it in place of every other route too
            (see the guard there). */}
        <Route path="/maintenance" component={MaintenancePage} />

        {import.meta.env.DEV && (
          <Route path="/__dev/listing-live" component={PostSubmitHubDevPreview} />
        )}
        {import.meta.env.DEV && (
          <Route path="/__dev/layout-harness" component={LayoutHarnessPage} />
        )}
        <Route path="/lp/:slug" component={LandingPageCatchAll} />
        <Route component={LandingPageCatchAll} />
      </Switch>
    </Suspense>
  );
}

/**
 * Keeps the persisted `appMode` and the URL in sync so the chrome (sidebar +
 * header toggle) always matches the page the user is looking at:
 *
 *   - Non-host on /dashboard/* → bounce to `/` (the user has no host UI).
 *   - Host on /dashboard/*    → auto-set mode to `'host'`.
 *   - Host on a guest-only    → auto-set mode to `'guest'` so a stale link
 *     route                     into /trips doesn't keep host chrome around
 *                               a guest page.
 *
 * This is the in-app realisation of "the arrow toggle is the only explicit
 * mode switcher" — every other navigation is reconciled with the user's role
 * on the destination route.
 */
function ModeRouteSync() {
  const [location, navigate] = useLocation();
  const { mode, setMode, canHost } = useAppMode();
  // We need both `isLoading` (auth + profile fetch in flight) AND `user`
  // to safely decide whether the current visitor is a host. Without this
  // gate, a real host hitting `/dashboard/whatever` on a fresh page load
  // gets bounced to `/` before their profile resolves — because while
  // `profile` is null, `canHost` is `false`.
  const { user, profile, isLoading } = useUser();
  const authReady = !isLoading && (!user || profile !== null);

  useEffect(() => {
    if (!authReady) return;
    if (isHostRoute(location)) {
      // Anonymous visitors fall through to the page's own RequireAuth
      // gate (which will open the auth modal). Don't pre-empt that here.
      if (!user) return;

      const isListingWizard =
        location === "/dashboard/listings/new" ||
        location.startsWith("/dashboard/listings/new/");

      const isListingEdit =
        location.startsWith("/dashboard/listings/") &&
        (/\/(edit|resume)\/?$/.test(location));

      if (!canHost) {
        if (!isListingWizard && !isListingEdit) {
          navigate("/", { replace: true });
        }
        return;
      }
      if (mode !== "host") setMode("host");
      return;
    }
    if (isGuestRoute(location) && canHost && mode !== "guest") {
      setMode("guest");
    }
  }, [authReady, user, location, mode, canHost, setMode, navigate]);

  return null;
}

/**
 * a11y — visually-hidden "skip to main content" link. Rendered as the first
 * focusable element in the public shells; becomes visible on keyboard focus and
 * jumps focus to the `<main id="main">` landmark.
 */
function SkipToMainLink() {
  const { t } = useTranslation();
  return (
    <a
      href="#main"
      className="sr-only rounded-md bg-background px-4 py-2 text-sm font-semibold text-foreground shadow-lg ring-2 ring-ring focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:inline-flex focus:min-h-11 focus:items-center"
    >
      {t("a11y.skip_to_main", { defaultValue: "Skip to main content" })}
    </a>
  );
}

/**
 * Status of the maintenance-bypass check for this browser. `"checking"` means
 * the in-flight server query has not resolved yet, so callers must treat it as
 * undecided (show a neutral loader) rather than as "locked" — that distinction
 * is what prevents a flash of the holding page before a valid bypass resolves.
 */
type MaintenanceAccessStatus = "checking" | "unlocked" | "locked";

/**
 * Reports whether this browser holds a valid maintenance bypass (the operator
 * entered the site-gate password on the holding page). Only queries the server
 * while `enabled` (maintenance ON and not already an authed admin). Returns a
 * three-state status so callers can distinguish "still checking" from
 * "checked: no bypass" and avoid treating the unresolved state as locked.
 */
function useMaintenanceUnlocked(enabled: boolean): MaintenanceAccessStatus {
  const [status, setStatus] = useState<MaintenanceAccessStatus>("checking");
  useEffect(() => {
    if (!enabled) {
      setStatus("locked");
      return;
    }
    let cancelled = false;
    setStatus("checking");
    getMaintenanceAccess().then((ok) => {
      if (!cancelled) setStatus(ok ? "unlocked" : "locked");
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return status;
}

function AppShell() {
  useAgentTools();
  useAgentHandoff();
  const [location] = useLocation();
  const prevLocation = useRef(location);
  const { mode: appMode } = useAppMode();
  // Task #4299 — client-side maintenance gate. Runs inside the SPA (i.e.
  // always after the site password gate), so when `feature_maintenance_mode`
  // is ON every visitor sees the holding page on EVERY route — only an
  // authenticated admin is let through (via the holding page's lock button).
  // /maintenance stays previewable regardless of the flag.
  const maintenanceEnabled = useFeatureFlag("feature_maintenance_mode");
  const flagsLoading = useFeatureFlagLoading();
  // Auth-based admin (NOT the path-based `isAdmin` used for layout below). Only
  // an authenticated admin is let past the maintenance gate (Task #4299). We
  // also track whether the authed-user/admin status is still loading so the
  // gate doesn't read a transient "not admin" while auth resolves.
  const { isAdmin: isAuthedAdmin, isLoading: userLoading } = useUser();
  // When maintenance mode is ON, a non-admin operator can reveal the full
  // live site by entering the shared site-gate password on the holding page
  // (see MaintenancePage). That sets an HttpOnly bypass cookie; we ask the
  // server whether this browser holds a valid bypass. The check exposes a
  // "checking" status so we can hold on a neutral loader while it is in flight
  // rather than flashing either the real site or the holding page.
  const maintenanceStatus = useMaintenanceUnlocked(
    maintenanceEnabled && !isAuthedAdmin,
  );
  useReferralCapture();

  // a11y — the <main> landmark that should receive focus on every client
  // route change so keyboard and screen-reader users land at the start of the
  // new page's content (paired with the "skip to main content" link below).
  const mainRef = useRef<HTMLElement | null>(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    if (prevLocation.current !== location) {
      prevLocation.current = location;
      window.scrollTo(0, 0);
    }
    trackPage(location);
  }, [location]);

  useEffect(() => {
    // Skip the very first mount so we don't steal focus on initial load; move
    // focus to the main landmark on subsequent route changes. preventScroll
    // keeps the scroll-to-top effect above authoritative.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
  }, [location]);

  // Task #3127 — /messages is shared by hosts and guests, but the chrome
  // now diverges by mode. Hosts keep the hosting sidebar (DashboardLayout
  // owns its own chrome, so we suppress the global Header for them — same
  // treatment as /dashboard/*). Renters render inside the same global
  // shell as /trips: top Header + centered content, no left sidebar. So
  // only host-mode messages opt out of the public Header branch.
  const isMessagesRoute = location.startsWith("/messages") || location === "/inbox" || location === "/inbox/";
  const isHostMessages = isMessagesRoute && appMode === "host";
  const isDashboard = location.startsWith("/dashboard") || isHostMessages;
  const isAdmin = location.startsWith("/admin");

  // The Shelby (Intercom) support Messenger is a customer-facing surface —
  // it must never float over the admin console (it covers action buttons in
  // the moderation side panel, and admins aren't its audience). Hide the
  // launcher for every /admin route and close any open Messenger panel;
  // restore the launcher as soon as the operator navigates back to the
  // public site. The override is also read by later `bootIntercom()` calls,
  // so it holds even when the widget script finishes loading after this
  // effect ran.
  useEffect(() => {
    if (!isAdmin) return;
    setLauncherVisibility(false);
    hideIntercom();
    return () => setLauncherVisibility(true);
  }, [isAdmin]);
  const isHomepage = location === "/" || location === "";
  // /auth, /login and /signup are now thin redirects to the global modal,
  // so they no longer need full-screen layout treatment.
  const isAuthPage =
    location === "/forgot-password" ||
    location === "/reset-password" ||
    location === "/auth/callback";
  const isFullScreenPage =
    location === "/list-your-car" ||
    location.startsWith("/dashboard/listings/new") ||
    /\/dashboard\/listings\/\d+\/resume/.test(location);

  // Trip detail page is rendered for both the renter route (`/trips/:id`)
  // The host detail route (`/dashboard/bookings/:id`) renders inside the
  // dashboard chrome (sidebar + back nav), so it intentionally does NOT
  // match here.
  const isTripDetail = /^\/trips\/[^/]+$/.test(location);

  // Mobile bottom tab bar (renter primary nav) is suppressed on routes that
  // already own a fixed bottom action bar (car detail's MobileBookingBar,
  // checkout) and on the full-screen chat thread. The bar itself is mobile-
  // only and renders nothing unless signed in.
  // Match an optional locale prefix (e.g. `/us/cars/...`). Most market
  // prefixes are stripped from wouter's `location` by the router base, but
  // `/us` is intentionally NOT stripped (parseLocaleFromPath rejects it), so
  // the bare `/^\/cars\/` test missed `/us/cars/...` — the global BottomTabBar
  // then stacked under CarDetail's own MobileBookingBar and covered the
  // booking CTA. The optional `/<two-letter>` segment makes this robust.
  const isCarDetail = /^(?:\/[a-z]{2})?\/cars\/[^/]+/.test(location);
  const isCheckout = location.startsWith("/checkout");
  const isMessageThread = /^\/messages\/[^/]+$/.test(location);
  const isBookingVerification = /^\/trips\/[^/]+\/verification\/?$/.test(location);
  const hideBottomTab = isCarDetail || isCheckout || isMessageThread || isBookingVerification || location === "/verify/return";

  // Task #4299 — when maintenance mode is ON, replace EVERY route with the
  // holding page (the whole site is gated). The ONLY way past the gate is to be
  // an authenticated admin — the holding page exposes a discreet lock button
  // that opens the auth modal, and once useUser().isAdmin is true the gate
  // lifts on every route (there is no path-based exemption). /maintenance
  // always renders through the Router (previewable regardless of the flag), and
  // /auth/callback is left through so an admin's OAuth sign-in can complete.
  const isMaintenanceRoute = location === "/maintenance";
  const isAuthCallbackRoute = location === "/auth/callback";
  // /maintenance always renders bare full-screen (no Header/Footer/banners),
  // regardless of the flag, so the holding page previews exactly as it ships.
  if (isMaintenanceRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MaintenancePage />
      </Suspense>
    );
  }
  // Task #4834 — hold rendering on a neutral loader while the inputs that
  // decide the maintenance gate are still resolving, so there is no flash of
  // the real site (gate not yet applied) NOR flash of the holding page (admin/
  // bypass not yet confirmed). The undecided inputs only matter when the gate
  // could actually engage: feature flags are loading, OR maintenance is ON and
  // we don't yet have a confirmed admin and the authed-user status or the
  // in-flight bypass check has not resolved. /auth/callback is exempt so an
  // admin's OAuth sign-in can complete. When maintenance is OFF (flags loaded)
  // the site renders immediately, exactly as before.
  const maintenanceGateUndecided =
    maintenanceEnabled &&
    !isAuthedAdmin &&
    (userLoading || maintenanceStatus === "checking");
  if (!isAuthCallbackRoute && (flagsLoading || maintenanceGateUndecided)) {
    return <PageLoader />;
  }
  if (
    maintenanceEnabled &&
    !isAuthedAdmin &&
    maintenanceStatus !== "unlocked" &&
    !isAuthCallbackRoute
  ) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MaintenancePage />
      </Suspense>
    );
  }

  if (isFullScreenPage || isAuthPage) {
    return (
      <>
        <ProdReadonlyBanner />
        <ImpersonationBanner />
        <Router />
      </>
    );
  }

  // Trip detail keeps its existing map-anchored split layout with the
  // public Header on top — its right-column map is positioned `top-[72px]`
  // relative to the viewport and depends on the Header being present.
  if (isTripDetail) {
    return (
      <>
        <SkipToMainLink />
        <ProdReadonlyBanner />
        <ImpersonationBanner />
        <SafeAreaTopFill />
        <div className="flex min-h-screen flex-col">
          <Header />
          <main
            ref={mainRef}
            id="main"
            tabIndex={-1}
            className="customer-page-canvas flex-1 pt-[calc(3.5rem_+_env(safe-area-inset-top))] outline-none md:pt-[72px]"
          >
            <Router />
          </main>
        </div>
      </>
    );
  }

  if (isDashboard) {
    return (
      <>
        <ProdReadonlyBanner />
        <ImpersonationBanner />
        <OwnerDashboardProvider>
          <Router />
        </OwnerDashboardProvider>
      </>
    );
  }


  if (isAdmin) {
    return (
      <>
        <ProdReadonlyBanner />
        <ImpersonationBanner />
        <Router />
      </>
    );
  }

  const isPreviewMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";

  // /account/payouts renders inside DashboardLayout (its own mobile top bar +
  // desktop host sidebar) but lives outside the /dashboard prefix, so it falls
  // through to this default shell which ALSO mounts the global Header — two
  // stacked headers on mobile, plus a redundant fixed-header offset. Suppress
  // the global header chrome here so DashboardLayout owns the top bar exactly
  // like every /dashboard route. BottomTabBar/Footer are intentionally kept.
  const usesDashboardChrome = location === "/account/payouts";

  return (
    <>
      <SkipToMainLink />
      <ProdReadonlyBanner />
      <ImpersonationBanner />
      {!usesDashboardChrome && <SafeAreaTopFill />}
      <div className="flex min-h-screen flex-col">
        {!usesDashboardChrome && <Header />}
        <main
          ref={mainRef}
          id="main"
          tabIndex={-1}
          className={cn("customer-page-canvas flex-1 outline-none", !isHomepage && !usesDashboardChrome && "pt-[calc(3.5rem_+_env(safe-area-inset-top))] md:pt-[72px]")}
        >
          <Router />
        </main>
        {!isPreviewMode && !location.startsWith("/bookings/") && !location.startsWith("/messages") && location !== "/inbox" && location !== "/inbox/" && <Footer />}
        {!isPreviewMode && !hideBottomTab && <BottomTabBar />}
      </div>
    </>
  );
}

/**
 * Inner router base = BASE_URL + locale prefix (task #865).
 *
 * Wouter accepts a reactive `base` prop; when the user switches markets via
 * the LanguageRegionModal we update the URL with `history.pushState` and
 * dispatch a synthetic `popstate`, which causes useLocation() (and therefore
 * this component) to re-render with the new prefix. All `<Route path="/...">`
 * declarations stay locale-agnostic — the base does the slicing for us.
 */
function LocaleAwareRouterBase({ children }: { children: React.ReactNode }) {
  const { market, language, innerPath } = useLocale();
  const baseAttr = useMemo(() => {
    const root = import.meta.env.BASE_URL.replace(/\/$/, "");
    // Admin and dashboard surfaces are operator-facing tools that live at a
    // single canonical path (/admin, /dashboard/*) regardless of the user's
    // chosen market. The LocaleProvider strips any leading /<market> from
    // these URLs via replaceState (see the locale-neutral effect there), so
    // by the time we render here the window pathname will already be the
    // bare /admin or /dashboard. Anchoring the router at the bare root then
    // makes wouter's matcher mount the routes correctly and any <Link> on
    // these pages emits unprefixed hrefs.
    if (isLocaleNeutralPath(innerPath)) return root;
    if (!market || market === "global") return root;
    // Mirror buildLocalePrefix() — keeps the routing layer in lock-step with
    // localizedHref() so internal links and the active route resolve to the
    // same canonical path.
    const langSegment =
      language && language.toLowerCase() !== marketDefaultLanguage(market).toLowerCase()
        ? `/${language}`
        : "";
    return `${root}/${market}${langSegment}`;
  }, [market, language, innerPath]);

  const ssrRequest = useContext(SsrRequestContext);
  const bootstrap = useSsrBootstrap();
  // Explicit default-language aliases (such as /uk/en/collection/…) retain
  // their actual request prefix for the frozen render. Using the normalized
  // /uk base would incorrectly route /en/collection to the loading fallback.
  const requestPath = ssrRequest ? ssrRequest.pathname.replace(/\/+$/, "") || "/" : "";
  const requestBase = ssrRequest?.flags && bootstrap
    ? `${import.meta.env.BASE_URL.replace(/\/$/, "")}${requestPath.endsWith(innerPath)
      ? requestPath.slice(0, -innerPath.length) : requestPath}`
    : baseAttr;
  return (
    <WouterRouter base={requestBase} ssrPath={ssrRequest ? ssrRequest.pathname : undefined}>
      {children}
    </WouterRouter>
  );
}

function marketDefaultLanguage(market: string): string {
  return MARKETS_BY_CODE[market]?.defaultLanguage ?? "en";
}

function EndBootstrapOnHydrate() {
  const request = useContext(SsrRequestContext);
  useEffect(() => { if (!request?.flags) endSsrBootstrap(); }, [request]);
  return null;
}

/**
 * The single app tree. The browser mounts it with the shared QueryClient; the
 * country-root SSR entry mounts the identical tree with a per-request client.
 */
export function AppTree({ queryClient = sharedQueryClient }: { queryClient?: QueryClient }) {
  return (
    <ThemeProvider>
      <EndBootstrapOnHydrate />
      <PlatformProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AuthProvider>
              <FeatureFlagProvider>
              {/* Task #8034 — bridges brand_cutover_driveshare into the
                  platform store; must live inside FeatureFlagProvider. */}
              <BrandCutoverSync />
              <LocaleProvider>
              <CurrencyProvider>
              <ImpersonationProvider>
              <AppModeProvider>
              <LocaleAwareRouterBase>
              <AuthModalProvider>
                <GuestBookingClaimCoordinator />
                <StepUpAuthDialog />
                <DsIconSprite />
                <PreferenceHydrator />
                <BrandRegistryBootstrap />
                <ErrorBoundary>
                  <RecoveryModeRedirector />
                  <ModeRouteSync />
                  <AppShell />
                  {/* Task #8007 — ConsentBanner is mounted ONCE at the root,
                      outside AppShell's per-route shell branches. Previously it
                      only rendered in the default and trip-detail shells, so
                      (a) a host landing on /dashboard/bookings from the
                      new-booking email could not consent on that route at all,
                      and (b) any navigation that switched shell branches
                      unmounted/remounted the banner and reset its `open`
                      state (candidate B in the task). A single root mount
                      shows the banner on every route and survives all SPA
                      navigations. */}
                  <ConsentBanner />
                  <RegionSuggestionBanner />
                </ErrorBoundary>
              </AuthModalProvider>
              </LocaleAwareRouterBase>
              </AppModeProvider>
              </ImpersonationProvider>
              </CurrencyProvider>
              </LocaleProvider>
              </FeatureFlagProvider>
            </AuthProvider>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </PlatformProvider>
    </ThemeProvider>
  );
}

function App() {
  return <AppTree />;
}

export default App;
